import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "../utils/api";
import { normalize } from "../utils/urls";

/**
 * Endpoint-agnostic useFetch
 * 
 * Signature preserved: returns [data] only, like your previous hook.
 * Reduces extra requests via: debounce, in-flight dedupe, and short-term cache.
 * 
 * Options:
 *  - defaultValue: any (default: null)
 *  - method: 'get' | 'post' | ... (default: 'get')
 *  - params: object of query params (merged below)
 *  - hasPaging: boolean (default false) -> if true, we'll add limit/offset but WILL NOT auto-iterate pages
 *  - defaultLimit: number (default 100 when hasPaging)
 *  - staleTime: number ms (default 20000) -> serve from cache if fresh
 *  - debounceMs: number ms (default 150) -> debounce URL/param churn
 *  - enabled: boolean (default true)
 */

// Module-level caches across component instances/rerenders
const inFlight = new Map(); // key -> promise
const dataCache = new Map(); // key -> { data, ts }

export const useFetch = (
  apiUri,
  {
    defaultValue = null,
    method = "get",
    hasPaging = false,
    defaultLimit = 100,
    params = undefined,
    staleTime = 20000,
    debounceMs = 150,
    enabled = true,
  } = {}
) => {
  const norm = useMemo(
    () =>
      normalize(apiUri, {
        method,
        params,
        hasPaging,
        defaultLimit,
        staleTime,
        debounceMs,
      }),
    [apiUri, method, params, hasPaging, defaultLimit, staleTime, debounceMs]
  );

  const [data, setData] = useState(() => {
    const hit = dataCache.get(norm.key);
    return hit ? hit.data : defaultValue;
  });

  const timerRef = useRef(null);
  const lastKeyRef = useRef(norm.key);

  const performFetch = useCallback(async (fetchKey, finalUrl) => {
    // Serve fresh cache if valid
    const cached = dataCache.get(fetchKey);
    const now = Date.now();
    if (cached && now - cached.ts < norm.staleTime) {
      setData(cached.data);
      return;
    }

    // Deduplicate concurrent requests
    if (inFlight.has(fetchKey)) {
      try {
        const resData = await inFlight.get(fetchKey);
        setData(resData);
      } catch {
        // swallow; data remains whatever it was
      }
      return;
    }

    const p = (async () => {
      try {
        const res = await api[norm.method](finalUrl);
        if (!res || res.status !== 200) {
          // return previous data on non-200 to avoid spamming retries
          return dataCache.get(fetchKey)?.data ?? defaultValue;
        }
        let payload = res.data;
        // Common API shape: { data, pagination, ... }
        if (payload && Object.prototype.hasOwnProperty.call(payload, "data")) {
          payload = payload.data;
        }
        return payload;
      } catch {
        // On error, return cached/default to avoid loops
        return dataCache.get(fetchKey)?.data ?? defaultValue;
      }
    })();

    inFlight.set(fetchKey, p);
    const json = await p.finally(() => inFlight.delete(fetchKey));
    dataCache.set(fetchKey, { data: json, ts: Date.now() });
    setData(json);
  }, [norm.staleTime, norm.method, defaultValue]);

  useEffect(() => {
    if (!enabled || !norm.endpoint) return;

    // Debounce URL/param churn
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const currentKey = norm.key;
    lastKeyRef.current = currentKey;

    timerRef.current = setTimeout(() => {
      // Only fetch if key hasn't changed during debounce window
      if (lastKeyRef.current === currentKey) {
        performFetch(currentKey, norm.url);
      }
    }, norm.debounceMs);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled, norm.key, norm.url, norm.debounceMs, norm.endpoint, performFetch]);

  return [data];
};
